import React, { useEffect } from "react";

export const groupDraftsByDate = (drafts = []) => {
  const grouped = {};

  drafts?.forEach(draft => {
    const draftDate = new Date(draft?.emails[draft?.emails?.length - 1]?.created_at);
    const today = new Date();

    const draftDateString = draftDate.toUTCString().split(" ").slice(0, 4).join(" ");
    const todayDateString = today.toUTCString().split(" ").slice(0, 4).join(" ");

    let key;

    if (draftDateString === todayDateString) {
      key = `Today, ${draftDate.toLocaleDateString("en-GB", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })}`;
    } else if (
      new Date(today.setUTCDate(today.getUTCDate() - 1)).toUTCString().split(" ").slice(0, 4).join(" ") ===
      draftDateString
    ) {
      key = `Yesterday, ${draftDate.toLocaleDateString("en-GB", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })}`;
    } else {
      key = draftDate.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      });
    }

    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(draft);
  });

  return grouped;
};

export const extractNameAndEmail = input => {
  if (!input) return { name: "Unknown Sender", email: "" };

  // Try to parse if it's a stringified object (with single quotes)
  if (typeof input === "string" && input.includes("{") && input.includes("emailAddress")) {
    try {
      const fixedJson = input
        .replace(/'/g, '"') // convert single to double quotes
        .replace(/([a-zA-Z0-9_]+):/g, '"$1":');
      const parsed = JSON.parse(fixedJson);
      if (parsed?.emailAddress?.address) {
        return {
          name: (parsed.emailAddress.name || parsed.emailAddress.address).trim(),
          email: parsed.emailAddress.address.trim(),
        };
      }
    } catch (e) {
      // Not valid JSON, fallback below
    }
  }

  // If already a JS object
  if (typeof input === "object" && input?.emailAddress?.address) {
    return {
      name: (input.emailAddress.name || input.emailAddress.address).trim(),
      email: input.emailAddress.address.trim(),
    };
  }

  // Handle "Name <email>" format
  const regex = /^(.*?)\s*<(.+?)>$/;
  const match = input?.match?.(regex);
  if (match) {
    return {
      name: match[1]?.trim(),
      email: match[2]?.trim(),
    };
  }

  // Handle plain email address
  if (typeof input === "string" && input.includes("@")) {
    return {
      name: input.trim(),
      email: input.trim(),
    };
  }
  return {
    name: "Unknown Sender",
    email: "",
  };
};

export const processDateRange = (startDate = null, endDate = null) => {
  let fromDate = null;
  let toDate = null;

  if (startDate) {
    // Send as explicit UTC to avoid timezone shifting issues
    // startDate is in format "YYYY-MM-DD"
    fromDate = startDate + "T00:00:00.000Z";
  }

  if (endDate) {
    // Send start of day in UTC - backend adds 1 day for end range
    // endDate is in format "YYYY-MM-DD"
    toDate = endDate + "T00:00:00.000Z";
  }

  return { fromDate, toDate };
};

// Helper function to get the value of 'giftcard' regardless of case and underscores
export const getGiftCardValue = refundDetails => {
  const giftcardKey = Object.keys(refundDetails || {}).find(key => key.replace(/_/g, "")?.toLowerCase() === "giftcard");
  return giftcardKey ? refundDetails[giftcardKey] : false;
};
// Helper function to get the value of 'issue' or 'solution' regardless of case and underscores
export const getDisputeValue = (disputeData, fieldName) => {
  const normalizedFieldName = fieldName.replace(/[_\s]/g, "")?.toLowerCase();
  const disputeKey = Object.keys(disputeData || {}).find(
    key => key.replace(/[_\s]/g, "")?.toLowerCase() === normalizedFieldName
  );
  return disputeKey ? disputeData[disputeKey] : ""; // Return empty string if no match
};
// Function to convert ArrayBuffer to Base64
export const arrayBufferToBase64 = buffer => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

export const SYSTEM_LABELS = [
  { key: "drafts", name: "Drafts", filter: "drafts" },
  { key: "inbox", name: "Inbox", filter: "all" },
  { key: "sent", name: "Sent", filter: "sent" },
  { key: "archive", name: "Archive", filter: "archive" },
  { key: "outbox", name: "Outbox", filter: "outbox" },
  { key: "agent-review", name: "Agent review", filter: "agent-review" },
];

export const BREAKPOINTS = {
  mobile: "(max-width: 639px)",
  // Small (sm) - Tablets and larger mobile screens
  mobileLarge: "(min-width: 640px) and (max-width: 767px)",
  // Medium (md) - Larger tablets
  tablet: "(min-width: 768px) and (max-width: 1023px)",
  // Large (lg) - Laptops and small desktops
  laptop: "(min-width: 1024px) and (max-width: 1279px)",
  // Extra Large (xl) - Desktops and large screens
  desktop: "(min-width: 1280px) and (max-width: 1535px)",
  // Ultra Large (2xl) - Extra-large desktops or 4K monitors
  desktopLarge: "(min-width: 1536px)",
};

export function useClickOutsideDropdown(dropdownRef, setActiveDropdown, multiplerefs = false) {
  const handleClickOutside = event => {
    if (multiplerefs) {
      // Multiple refs case
      if (!dropdownRef.current.some(ref => ref && ref.contains(event.target))) {
        setActiveDropdown(null); // Close all dropdowns
      }
    } else {
      // Single ref case
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null); // Close the dropdown
      }
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setActiveDropdown, multiplerefs, dropdownRef]);
}

export const statusSvgGenerator = color => {
  const svgString = `
    <svg xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
      <circle cx="3.5" cy="3.5" r="3.5" fill="${color}" />
    </svg>
  `;
  const base64EncodedSvg = btoa(unescape(encodeURIComponent(svgString)));
  const dataURI = `data:image/svg+xml;base64,${base64EncodedSvg}`;

  return dataURI;
};

export const findLastDraftId = emails => {
  // Iterate from the last email to the first
  for (let i = emails.length - 1; i >= 0; i--) {
    if (emails[i]?.drafts && emails[i]?.drafts?.length > 0) {
      // Return the last draft's id from this email
      return emails[i]?.drafts[emails[i]?.drafts?.length - 1]?.id;
    }
  }
  return null;
};
